import { useCallIntelligenceWritebackV1QueryUpdaters } from '../query-updaters';
import { WritebackCallsIO } from '../types';
import {
  UseCallIntelligenceWritebackV1MutationEndpointArgs,
  useCallIntelligenceWritebackV1Mutation,
} from './use-call-intelligence-writeback-v1-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `WritebackCalls` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useWritebackCallsMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends WritebackCallsIO['input'] = WritebackCallsIO['input']
>({
  options,
  optimisticUpdate = false,
  ...args
}: UseCallIntelligenceWritebackV1MutationEndpointArgs<
  'WritebackCalls',
  E,
  C | undefined,
  OtherOptions,
  RequestOverride
> = {}) => {
  const { invalidateQueries } = useCallIntelligenceWritebackV1QueryUpdaters();
  return useCallIntelligenceWritebackV1Mutation<'WritebackCalls', E, MutationContext<C>, OtherOptions, RequestOverride>(
    {
      endpointName: 'WritebackCalls',
      ...args,
      options: {
        ...options,
        onMutate: async (request) => {
          if (optimisticUpdate) {
            // Implement optimistic updates here
          }

          return {
            // Add custom context type here (if exists in `MutationContext`)
            otherContext: await options?.onMutate?.(request),
          };
        },
        onSuccess: (response, request, context) => {
          // Implement query updates or invalidation for affected queries here
          invalidateQueries({
            endpointName: 'CallWritebackStatus',
            queryFilters: {
              predicate(query) {
                const queryKeyCallId = query.queryKey[2]?.callId;
                if (queryKeyCallId && request.callId) {
                  return queryKeyCallId === request.callId;
                }
                return false;
              },
            },
          });

          return options?.onSuccess?.(response, request, context?.otherContext);
        },
        onError: (error, request, context) => {
          // Revert optimistic update here (if implemented)

          return options?.onError?.(error, request, context?.otherContext);
        },
        onSettled: (response, error, request, context) => {
          // Only pass context of type C into provided `onSettled` option
          return options?.onSettled?.(response, error, request, context?.otherContext);
        },
      },
    }
  );
};
